import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, ButtonType } from "components/Button/Button";
import { DataFetcher } from "components/DataFetcher";
import Pod from "components/Pod/Pod";
import useScrollToPageArea from "hooks/useScrollToPageArea";
import CourseListPane from "./components/CourseListPane";
import PreviewControlPanel from "./components/PreviewControlPanel";
import "./CoursePreview.scss";

const parseNullableInt = (value) => {
	if (value == null || value === "") {
		return null;
	}

	const parsed = Number(value);

	if (Number.isNaN(parsed)) {
		return null;
	}

	return parsed;
};

const CoursePreview = () => {
	const { search, hash } = useLocation();
	const navigate = useNavigate();
	const { scroll } = useScrollToPageArea();
	const [data, setData] = useState(null);
	const [previewLockedLearningJourneys, setPreviewLockedLearningJourneys] = useState(false);

	const searchParams = useMemo(() => new URLSearchParams(search), [search]);
	const draftId = parseNullableInt(searchParams.get("draftId"));
	const courseId = parseNullableInt(searchParams.get("courseId"));

	const fetchData = useMemo(() => {
		if (draftId != null) {
			return {
				url: `/api/v3/CoursePreview/${draftId}`
			};
		}

		if (courseId != null) {
			return {
				url: `/api/v3/CoursePreview/ByCourse/${courseId}`
			};
		}

		return null;
	}, [draftId, courseId]);

	const courses = useMemo(() => {
		if (!data) {
			return [];
		}

		return Array.isArray(data) ? data : [data];
	}, [data]);

	const effectiveDraftId = useMemo(() => {
		if (draftId != null) {
			return draftId;
		}
		if (!courses || courses.length === 0) {
			return null;
		}
		return courses[0]?.DraftId ?? null;
	}, [draftId, courses]);

	const buildExitUrl = () => {
		if (courseId == null && effectiveDraftId == null) {
			return "/contentcreation";
		}

		if (courseId != null && courseId >= 0) {
			return `/editcourse?courseId=${courseId}`;
		}

		return `/editcourse?draftId=${effectiveDraftId}`;
	};

	const handleExitPreview = () => {
		navigate(buildExitUrl());
	};

	if (!fetchData) {
		return (
			<div className="course-preview">
				<div className="grid-two-col gutters">
					<Pod title="Course Preview">
						<div className="course-preview-empty">
							<p>Unable to determine which course draft to preview.</p>
							<Button
								type={ButtonType.Primary}
								text="Back to Content Creation"
								to="/contentcreation"
							/>
						</div>
					</Pod>

					<Pod title="Preview Control Panel" mobileOrder={1} sticky>
						<PreviewControlPanel
							onExitPreview={handleExitPreview}
							setPreviewLockedLearningJourneys={setPreviewLockedLearningJourneys}
						/>
					</Pod>
				</div>
			</div>
		);
	}

	return (
		<DataFetcher fetchData={fetchData} setData={setData}>
			<div className="course-preview">
				<div className="grid-two-col gutters">
					<CourseListPane
						title="Course Preview"
						courses={courses}
						hash={hash}
						scroll={scroll}
						previewMode={true}
						previewLockedLearningJourneys={previewLockedLearningJourneys}
						draftId={effectiveDraftId}
					/>

					<Pod title="Preview Control Panel" mobileOrder={1} sticky>
						<PreviewControlPanel
							onExitPreview={handleExitPreview}
							setPreviewLockedLearningJourneys={setPreviewLockedLearningJourneys}
						/>
					</Pod>
				</div>
			</div>
		</DataFetcher>
	);
};

export default CoursePreview;